import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { fetchNominasTarjetas } from "../../api/Nominas/NominasApi";
import {
  esFinDeSemanaOFiesta,
  formatearFechaCorta,
} from "../../utils/fechasFestivas";

const MESES_LABEL = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

const TIPOS = [
  { value: "revolut", label: "Revolut", color: "secondary" },
  { value: "credito", label: "Crédito", color: "warning" },
  { value: "debito", label: "Débito", color: "info" },
];

const TIPO_LABEL = {
  debito: "débito",
  credito: "crédito",
  revolut: "Revolut",
};

const CeldaMesDetalle = ({
  mes,
  valor,
  detalle,
  titulo,
  onVerDetalle,
  formatearImporte,
  sx = {},
  fontSize = "0.8rem",
  fontWeight = 700,
}) => {
  const lineas = detalle?.[String(mes)] || [];
  const tieneDetalle = valor > 0 && lineas.length > 0;

  if (!valor) {
    return (
      <TableCell align="right" sx={{ fontSize, fontWeight, ...sx }}>
        —
      </TableCell>
    );
  }

  return (
    <TableCell align="right" sx={{ fontSize, fontWeight, ...sx }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={0.25}
        sx={{
          "& .detalle-btn": { opacity: tieneDetalle ? 0.5 : 0 },
          "&:hover .detalle-btn": { opacity: tieneDetalle ? 1 : 0 },
        }}
      >
        <span>{formatearImporte(valor)}</span>
        {tieneDetalle ? (
          <Tooltip title="Ver movimientos">
            <IconButton
              className="detalle-btn"
              size="small"
              onClick={() =>
                onVerDetalle({ titulo, lineas, valor })
              }
              sx={{ p: 0.25, ml: 0.25 }}
              aria-label={`Ver gastos ${titulo}`}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </TableCell>
  );
};

const DialogDetalleMes = ({ open, onClose, data, formatearImporte }) => {
  const theme = useTheme();
  if (!data) return null;
  const { titulo, lineas, valor } = data;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={800}>
          {titulo}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {lineas.length} {lineas.length === 1 ? "movimiento" : "movimientos"}{" "}
          · Total {formatearImporte(valor)}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb: 1.5,
            py: 0.75,
            borderRadius: 2,
            alignItems: "flex-start",
            bgcolor: alpha(theme.palette.error.main, 0.05),
            borderColor: alpha(theme.palette.error.main, 0.28),
            color: "text.secondary",
            "& .MuiAlert-icon": {
              color: theme.palette.error.main,
              mt: 0.15,
              fontSize: "1.15rem",
            },
            "& .MuiAlert-message": { width: "100%", py: 0.25 },
          }}
        >
          <Typography variant="caption" component="span" sx={{ lineHeight: 1.5 }}>
            <Box component="span" sx={{ color: "error.main", fontWeight: 700 }}>
              Rojo
            </Box>{" "}
            si es sábado, domingo o festivo en Catalunya (Reyes, Sant Joan,
            Diada, Sant Esteve, Viernes Santo, Pascua, etc.)
          </Typography>
        </Alert>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 72 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, width: 100 }}>
                Importe
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.map((linea, idx) => {
              const festivo = esFinDeSemanaOFiesta(linea.fecha);
              const abono = linea.importe < 0;
              const importeColor = abono
                ? "success.main"
                : festivo
                  ? "error.main"
                  : "text.primary";
              return (
                <TableRow
                  key={`${linea.fecha || ""}-${linea.concepto}-${idx}`}
                  hover
                  sx={festivo ? { bgcolor: "rgba(211, 47, 47, 0.06)" } : undefined}
                >
                  <TableCell
                    sx={{
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      fontWeight: festivo ? 700 : 400,
                      color: festivo ? "error.main" : "text.secondary",
                    }}
                  >
                    {formatearFechaCorta(linea.fecha)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.85rem",
                      wordBreak: "break-word",
                      color: festivo ? "error.main" : "text.primary",
                    }}
                  >
                    {linea.concepto}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      color: importeColor,
                    }}
                  >
                    {formatearImporte(linea.importe)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

const NominasTarjetas = ({
  year,
  empresa,
  formatearImporte,
  embedded = false,
}) => {
  const theme = useTheme();
  const [tipo, setTipo] = useState("revolut");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [detallePopup, setDetallePopup] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNominasTarjetas({ tipo, year, empresa });
      setData(res);
      setExpanded({});
      setDetallePopup(null);
    } catch (e) {
      setData(null);
      setError(
        e.response?.data?.error ||
          e.message ||
          "No se pudieron cargar los gastos de tarjeta",
      );
    } finally {
      setLoading(false);
    }
  }, [tipo, year, empresa]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const trabajadores = data?.trabajadores || [];
  const total = data?.total;
  const meta = data?.meta;

  const stickyCell = {
    position: "sticky",
    left: 0,
    zIndex: 2,
    bgcolor: theme.palette.background.paper,
  };

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const accent =
    tipo === "revolut"
      ? theme.palette.secondary.main
      : tipo === "credito"
        ? theme.palette.warning.main
        : theme.palette.info.main;

  const renderTabla = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">
            Cargando tarjetas {TIPO_LABEL[tipo]}…
          </Typography>
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (!trabajadores.length) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Sin movimientos de tarjeta {TIPO_LABEL[tipo]} para {year}.
          </Typography>
          {meta?.fuente ? (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              Fuente: {meta.fuente}
            </Typography>
          ) : null}
          {meta?.nota ? (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              {meta.nota}
            </Typography>
          ) : null}
        </Box>
      );
    }

    return (
      <TableContainer
        sx={{
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.6),
          maxHeight: embedded ? "min(480px, 55vh)" : "60vh",
          overflow: "auto",
          width: "100%",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: embedded ? "auto" : "fixed",
            minWidth: embedded ? 960 : 1400,
          }}
        >
          <colgroup>
            <col style={{ width: embedded ? 220 : 280 }} />
            {MESES_LABEL.map((label) => (
              <col key={label} style={{ width: embedded ? 68 : 80 }} />
            ))}
            <col style={{ width: embedded ? 88 : 100 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyCell,
                  zIndex: 3,
                  fontWeight: 800,
                }}
              >
                Titular / tarjeta
              </TableCell>
              {MESES_LABEL.map((label) => (
                <TableCell
                  key={label}
                  align="right"
                  sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {label}
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{
                  fontWeight: 800,
                  bgcolor: alpha(accent, 0.08),
                  borderLeft: `2px solid ${theme.palette.divider}`,
                }}
              >
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trabajadores.map((trab) => {
              const open = !!expanded[trab.id];
              const hasVarias = (trab.tarjetas?.length || 0) > 1;
              return (
                <React.Fragment key={trab.id}>
                  <TableRow
                    hover
                    sx={{ "& > *": { borderBottom: open ? 0 : undefined } }}
                  >
                    <TableCell
                      sx={{
                        ...stickyCell,
                        fontWeight: 700,
                        fontSize: "0.85rem",
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {hasVarias ? (
                          <IconButton
                            size="small"
                            onClick={() => toggle(trab.id)}
                          >
                            {open ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        ) : (
                          <Box sx={{ width: 28 }} />
                        )}
                        <span>{trab.label}</span>
                        {hasVarias ? (
                          <Chip
                            size="small"
                            label={`${trab.tarjetas.length} tarjetas`}
                            sx={{ ml: 0.5, height: 20, fontSize: "0.65rem" }}
                          />
                        ) : null}
                      </Stack>
                    </TableCell>
                    {MESES_LABEL.map((label, idx) => {
                      const mes = idx + 1;
                      const v = trab.meses[String(mes)] || 0;
                      return (
                        <CeldaMesDetalle
                          key={mes}
                          mes={mes}
                          valor={v}
                          detalle={trab.detalle}
                          titulo={`${trab.label} — ${label} ${year}`}
                          onVerDetalle={setDetallePopup}
                          formatearImporte={formatearImporte}
                        />
                      );
                    })}
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 800,
                        borderLeft: `2px solid ${theme.palette.divider}`,
                        bgcolor: alpha(accent, 0.05),
                      }}
                    >
                      {formatearImporte(trab.total)}
                    </TableCell>
                  </TableRow>
                  {hasVarias && open
                    ? trab.tarjetas.map((card) => (
                        <TableRow key={card.id} sx={{ bgcolor: alpha(accent, 0.02) }}>
                          <TableCell
                            sx={{
                              ...stickyCell,
                              py: 0.75,
                              fontSize: "0.75rem",
                              color: "text.secondary",
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Box sx={{ width: 28, flexShrink: 0 }} />
                              <Box sx={{ minWidth: 0 }}>
                                {card.label}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{ ml: 1, fontFamily: "monospace" }}
                                >
                                  {card.cuenta}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          {MESES_LABEL.map((label, idx) => {
                            const mes = idx + 1;
                            const v = card.meses[String(mes)] || 0;
                            return (
                              <CeldaMesDetalle
                                key={mes}
                                mes={mes}
                                valor={v}
                                detalle={card.detalle}
                                titulo={`${card.label} — ${label} ${year}`}
                                onVerDetalle={setDetallePopup}
                                formatearImporte={formatearImporte}
                                fontSize="0.75rem"
                                fontWeight={400}
                                sx={{ py: 0.75 }}
                              />
                            );
                          })}
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: "0.75rem",
                              py: 0.75,
                              borderLeft: `2px solid ${theme.palette.divider}`,
                            }}
                          >
                            {formatearImporte(card.total)}
                          </TableCell>
                        </TableRow>
                      ))
                    : null}
                </React.Fragment>
              );
            })}
            {total ? (
              <TableRow
                sx={{
                  bgcolor: alpha(accent, 0.12),
                  "& .MuiTableCell-root": { fontWeight: 800 },
                }}
              >
                <TableCell
                  sx={{
                    ...stickyCell,
                    bgcolor: alpha(accent, 0.12),
                  }}
                >
                  Total tarjetas {TIPO_LABEL[tipo]}
                </TableCell>
                {MESES_LABEL.map((label, idx) => {
                  const mes = idx + 1;
                  const v = total.meses[String(mes)] || 0;
                  return (
                    <CeldaMesDetalle
                      key={mes}
                      mes={mes}
                      valor={v}
                      detalle={total.detalle}
                      titulo={`Total tarjetas ${TIPO_LABEL[tipo]} — ${label} ${year}`}
                      onVerDetalle={setDetallePopup}
                      formatearImporte={formatearImporte}
                      fontWeight={800}
                    />
                  );
                })}
                <TableCell
                  align="right"
                  sx={{ borderLeft: `2px solid ${theme.palette.divider}` }}
                >
                  {formatearImporte(total.total)}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Paper
      sx={{
        mt: embedded ? 0 : 2,
        p: { xs: 1.5, sm: embedded ? 1.5 : 2 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.8),
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(160deg, ${alpha(accent, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 50%)`
            : `linear-gradient(160deg, ${alpha(accent, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 55%)`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 28px rgba(0,0,0,0.22)"
            : "0 10px 40px rgba(15,23,42,0.05)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <CreditCardOutlinedIcon sx={{ color: accent, fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: -0.2 }}>
              Gastos en tarjetas
            </Typography>
          </Stack>
          <Chip
            size="small"
            variant="outlined"
            label={`${TIPO_LABEL[tipo]} · ${year}`}
            sx={{
              mt: 0.75,
              height: 22,
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: alpha(accent, 0.35),
              bgcolor: alpha(accent, 0.04),
            }}
          />
          {meta?.nota && !embedded ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {meta.nota}
            </Typography>
          ) : null}
        </Box>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          {TIPOS.map((opt) => {
            const selected = tipo === opt.value;
            const main = theme.palette[opt.color].main;
            return (
              <Chip
                key={opt.value}
                label={opt.label}
                clickable
                onClick={() => setTipo(opt.value)}
                variant={selected ? "filled" : "outlined"}
                color={opt.color}
                sx={{
                  fontWeight: 700,
                  height: 32,
                  fontSize: "0.8125rem",
                  letterSpacing: "0.01em",
                  transition: "all 0.2s ease",
                  ...(selected
                    ? {
                        boxShadow: `0 4px 14px ${alpha(main, 0.38)}`,
                        "&:hover": { filter: "brightness(1.06)" },
                      }
                    : {
                        bgcolor: alpha(main, 0.07),
                        borderColor: alpha(main, 0.32),
                        color: main,
                        "&:hover": {
                          bgcolor: alpha(main, 0.14),
                          borderColor: alpha(main, 0.5),
                        },
                      }),
                }}
              />
            );
          })}
          {total?.total ? (
            <Chip
              size="small"
              variant="outlined"
              label={`Total anual: ${formatearImporte(total.total)}`}
              sx={{ fontWeight: 700, borderColor: alpha(accent, 0.5) }}
            />
          ) : null}
        </Stack>
      </Stack>

      {renderTabla()}
      <DialogDetalleMes
        open={!!detallePopup}
        onClose={() => setDetallePopup(null)}
        data={detallePopup}
        formatearImporte={formatearImporte}
      />
    </Paper>
  );
};

export default NominasTarjetas;
